/**
 * The rules a hand-built basket obeys — ROADMAP 9.66.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A BASKET IS A SET OF CONTRACTS, A STRATEGY IS A SET OF RULES
 *
 * `StrategyLeg` stores a CRITERION — "the call two strikes above the money" —
 * and `resolveLegs` turns it into an instrument at the moment of firing, from
 * whatever the chain says the money is then. That is what makes a saved Iron
 * Condor placeable on any day at any spot.
 *
 * A basket is the other thing. The trader has already looked at the ladder and
 * chosen 24,600 CE, so the leg IS that contract: no criterion, no resolution,
 * no ATM to be re-derived at fire time. The two are kept apart for the same
 * reason `basketPayoff.ts` is kept apart from the strategy glyph — one is a
 * shape that is valid for every index at once, the other is a specific claim
 * about specific instruments.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE ROW PER CONTRACT, AND IT NETS
 *
 * Adding the same contract twice does not make two legs. It makes one leg with
 * more lots, and adding the OTHER side takes lots away — which is exactly what
 * the Positions drawer shows for the same two clicks, and the whole reason the
 * trader can read a basket the way they read a position.
 *
 * It is also forced. `deploy.service.ts` refuses a payload where "the same
 * contract appears in two legs", and `openTrade` would otherwise open a Trade
 * holding +2 and −1 of one token as two legs that can never be exited
 * independently. The database enforces it too — `@@unique([basketId, listingId])`
 * — so this function and that constraint are one rule stated twice rather than
 * two rules that can disagree.
 *
 * A LEG THAT NETS TO ZERO IS REMOVED, not kept at zero lots. Zero lots is not
 * an order, and a row that cannot be placed sitting in a basket of rows that
 * can is the kind of thing that gets counted.
 * ─────────────────────────────────────────────────────────────────────────────
 */
/**
 * The most legs one hand-built basket may carry.
 *
 * TEN, against `INTENT_LIMITS.maxLegs` of forty. The wire limit answers "what
 * could any composed entry be"; this answers "what can a person assemble by
 * clicking a ladder and still read before they send it". The owner set it at
 * ten and it is a product decision rather than a safety one — the order path
 * is unchanged and would accept more.
 */
export const BASKET_MAX_LEGS = 10;
/** Longest a basket's name may be — the same ceiling `Trade.label` has. */
export const BASKET_NAME_MAX = 48;
/**
 * Add lots of one contract to a basket, netting against what is already there.
 *
 * The incoming leg carries whatever extra fields the caller's leg type has
 * (strike, expiry, the option type) and they are kept as given when the leg is
 * new. On an EXISTING token they are not re-read: the token already names the
 * contract, so a second description of it is a second thing that can be wrong.
 */
export function addToBasket(legs, incoming) {
    if (!Number.isInteger(incoming.token) || incoming.token <= 0) {
        return { ok: false, why: 'That strike is not listed.' };
    }
    if (!Number.isInteger(incoming.lots) || incoming.lots < 1) {
        return { ok: false, why: 'Set a size first.' };
    }
    const at = legs.findIndex((l) => l.token === incoming.token);
    if (at === -1) {
        if (legs.length >= BASKET_MAX_LEGS) {
            return {
                ok: false,
                why: `A basket holds ${BASKET_MAX_LEGS} legs. Remove one to add another.`,
            };
        }
        return { ok: true, legs: [...legs, incoming] };
    }
    const held = legs[at];
    /*
     * Signed, so one subtraction covers all four combinations. Same side adds,
     * opposite side reduces, and the sign of the answer is the side that is
     * left — which is how a position behaves and therefore how this must.
     */
    const signed = (held.side === 'BUY' ? held.lots : -held.lots) +
        (incoming.side === 'BUY' ? incoming.lots : -incoming.lots);
    if (signed === 0) {
        return { ok: true, legs: legs.filter((_, i) => i !== at) };
    }
    const next = {
        ...held,
        side: signed > 0 ? 'BUY' : 'SELL',
        lots: Math.abs(signed),
    };
    return { ok: true, legs: legs.map((l, i) => (i === at ? next : l)) };
}
/**
 * Re-size one leg directly, as the stepper does.
 *
 * SEPARATE FROM `addToBasket` because it is a different intention: the stepper
 * states the size the leg should BE, while a ladder click states a size to
 * add. Routing the stepper through the adder would make "set this leg to 1"
 * mean "add one more lot", which is the bug that writes itself.
 *
 * Zero removes the leg, for the reason the header gives.
 */
export function setBasketLots(legs, token, lots) {
    if (!Number.isInteger(lots) || lots < 0)
        return legs;
    if (lots === 0)
        return legs.filter((l) => l.token !== token);
    return legs.map((l) => (l.token === token ? { ...l, lots } : l));
}
/** Drop one leg. */
export function removeFromBasket(legs, token) {
    return legs.filter((l) => l.token !== token);
}
/**
 * What a new basket is called before the trader names it — "NIFTY BASKET (3)".
 *
 * The number is WHICH BASKET THIS IS, not how many legs it has. A name that
 * counted legs would change under the trader every time they added one, and
 * the name is the label the Trade is placed under — it has to be the same
 * thing when they press the button as when they read it.
 *
 * The sequence is assigned server-side at creation and is unique among the
 * baskets that EXIST — `@@unique([userId, underlying, seq])` enforces it — so
 * two baskets a trader holds can never share a default name. It is not a
 * permanent identifier: delete the highest-numbered basket and the next one
 * created takes that number back. Nothing depends on it being permanent, and
 * a counter table to prevent a collision with something that no longer exists
 * would be machinery for a cosmetic default.
 */
export function defaultBasketName(underlying, seq) {
    return `${underlying} BASKET (${seq})`.slice(0, BASKET_NAME_MAX);
}
//# sourceMappingURL=basketDraft.js.map